<template>
  <ValidatedInput
    rules="min:2|max:50"
    :model-value="vatNumber"
    :title="$t('VAT title')"
    :label="label"
    :name="name"
    :warning-message="isVatValidationEnabled ? validationMessages[0] : ''"
    :usage-tip="isVatValidationEnabled ? validationMessages[1] : ''"
    :success-message="successMessage"
    :loading="inputLoading"
    :data-vat_val_result="dataMessage"
    :prefix="vatPrefix"
    @update:model-value="handleVatInput"
    @blur="passed => onValidate(passed)"
  >
    <template #default="{ passed }">
      <CustomButton
        v-if="isVatValidationEnabled && isErrorCouldNotValidate"
        :text="$t('Validate my VAT ID')"
        type="primary"
        :loading="buttonLoading"
        data-vat_val_button
        class="mb-8"
        @click="onValidate(passed, true)"
      >
        <template #icon-right>
          <div @click="openVatValidationModal">
            <span class="iconify svg--account text-2xl pl-2" />
            <span class="iconify svg--checkmark text-xxs" />
          </div>
        </template>
      </CustomButton>
      <VatIdNeedSupport
        v-if="isVatValidationEnabled && isValidationSupportVisible"
        :show="isErrorNotValid"
        @click="openVatValidationModal"
      />
      <VatValidationModalInfo
        v-if="isVatValidationModalOpen && isVatValidationEnabled"
        @close="closeVatValidationModal"
      />
    </template>
  </ValidatedInput>
</template>

<script lang="ts">


import formatVatNumberForLocale from '~~/helpers/formatVatNumberForLocale';
import { VAT_FORMAT } from '~~/constants/checkout';

const DATA_MESSAGE_TYPE = {
  success: 'vat_val_success',
  failed: 'vat_val_failed',
  failedNoRate: 'vat_val_failed_norate',
  triggered: 'vat_val_triggered',
  buttonClick: 'vat_val_button_click',
  timeout: 'vat_val_timeout',
  noAddress: 'vat_val_no_address',
} as const;

export default defineComponent({
  name: 'VatIdInput',

  props: {
    name: {
      type: String,
      required: true,
    },
    vatNumber: {
      type: String,
      default: '',
    },
    shouldValidateVat: {
      type: Boolean,
      default: true,
    },
    showValidationSupport: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['input', 'address-autofill', 'address-clear'],
  setup(props, { emit }) {

    const i18n = useI18n();
    const { countryCode } = useLocalization();
    const { isChangeTaxRateEnabled } = useStoreVsfExtended();
    const isVatValidationModalOpen = ref<boolean>(false);
    const openVatValidationModal = () => {

      isVatValidationModalOpen.value = true;

    };
    const closeVatValidationModal = () => {

      isVatValidationModalOpen.value = false;

    };
    const {
      messages: validationMessages,
      areVatDetailsValid,
      isVatNumberValid,
      isErrorCouldNotValidate,
      loading,
      isErrorNotValid,
      isNetPriceActive,
      vatPrefix,
      companyAddress,
      hasMissingRegistryAddress,
      shouldClearRegistryAddress,
      validateVatNumber,
    } = useVatValidation();

    const processedVatNumber = computed(() => formatVatNumberForLocale(countryCode.value, props.vatNumber));

    const isVatValidationEnabled = computed(() => props.shouldValidateVat);
    const isValidationSupportVisible = computed(() => props.showValidationSupport);

    const successMessage = computed(() =>
      isVatValidationEnabled.value && isVatNumberValid.value ? i18n.t('VAT ID successfully validated.') : '');
    const inputLoading = computed(() =>
      isVatValidationEnabled.value && loading.value && !isErrorCouldNotValidate.value);
    const buttonLoading = computed(() =>
      isVatValidationEnabled.value && loading.value && isErrorCouldNotValidate.value);

    const dataMessage = computed(() => {

      if (!isVatValidationEnabled.value) {

        return '';

      }

      if (inputLoading.value) {

        return DATA_MESSAGE_TYPE.triggered;

      }

      if (buttonLoading.value) {

        return DATA_MESSAGE_TYPE.buttonClick;

      }

      if (areVatDetailsValid.value) {

        if (isChangeTaxRateEnabled.value && !isNetPriceActive.value) {

          return DATA_MESSAGE_TYPE.failedNoRate;

        }

        return DATA_MESSAGE_TYPE.success;

      }

      if (hasMissingRegistryAddress.value) {

        return DATA_MESSAGE_TYPE.noAddress;

      }

      if (isErrorCouldNotValidate.value) {

        return DATA_MESSAGE_TYPE.timeout;

      }

      if (isErrorNotValid.value) {

        return DATA_MESSAGE_TYPE.failed;

      }

      return '';

    });

    const handleVatInput = (vatInput: string | number) => {

      emit('input', String(vatInput));

    };
    const onValidate = async (passed: boolean, isVatValButton = false) => {

      if ((!passed && !isVatValButton) || !props.shouldValidateVat) {

        return;

      }
      const hasValidated = await validateVatNumber({
        vatNumber: processedVatNumber.value,
        forceValidate: isVatValButton,
      });

      handleVatInput(processedVatNumber.value);

      // A stored result was already acted on when it landed; acting on it again would overwrite
      // the edits the user made since.
      if (!hasValidated) {

        return;

      }

      if (shouldClearRegistryAddress.value) {

        emit('address-clear');

        return;

      }

      if (companyAddress.value) {

        emit('address-autofill', companyAddress.value);

      }

    };

    const label = computed(() => `${VAT_FORMAT[countryCode.value]?.placeholder}`);

    return {
      onValidate,
      dataMessage,
      buttonLoading,
      inputLoading,
      isErrorCouldNotValidate,
      areVatDetailsValid,
      successMessage,
      validationMessages,
      processedVatNumber,
      handleVatInput,
      label,
      vatPrefix,
      isErrorNotValid,
      isVatValidationModalOpen,
      closeVatValidationModal,
      openVatValidationModal,
      isVatValidationEnabled,
      isValidationSupportVisible,
    };

  },
});

</script>
